import { FC } from 'react';
import { differenceInMonths, parseISO, format } from 'date-fns';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';
import { ReactNode } from 'react';

interface CreditScoreChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
  hideHeader?: boolean;
}

// Define a type for the credit score data
interface CreditScoreData {
  month: string;
  credit_score: number;
}

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

// Mock credit score generation based on financial health - using MemberCard.tsx implementation
const calculateMockCreditScore = (stats: FinancialHealthStats[]): number => {
  if (!stats.length) return 620; // Default fallback

  // Sort stats by date ascending
  const sortedStats = [...stats].sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  );

  // Get the latest stats
  const latest = sortedStats[sortedStats.length - 1];
  if (!latest) return 620;

  // FOR DEMO: Add more contrast between VeraScore and credit score
  // Deliberately make credit scores lower for members with good VeraScores
  const veraScore = latest.verascore ?? 0;
  let baseScore;
  
  // Lower the threshold to 55 to get more demo examples
  // For members with VeraScore > 55, deliberately lower the credit score
  // to show how our system unlocks opportunities
  if (veraScore >= 55) {
    // High VeraScore, but we'll make their credit score relatively low
    // to show how our system unlocks opportunities
    // Use a deterministic "random" value based on UUID to prevent re-rendering issues
    const deterministicRandom = latest.uuid 
      ? (parseInt(latest.uuid.replace(/[^0-9]/g, ''), 10) % 80) 
      : 40;
    baseScore = 520 + deterministicRandom; // Range 520-600
  } else {
    // For lower VeraScores, keep the calculation similar to before
    baseScore = 300 + Math.round((veraScore / 100) * 520);
  }

  // Payment history impact (-100 to +50 points)
  const paymentImpact = Math.min(
    50,
    -20 * (latest.number_of_late_payments ?? 0) +
      Math.min(50, 5 * (latest.age_most_recent_late ?? 0))
  );

  // DTI impact (-50 to +50 points)
  const dtiRatio = latest.dti_ratio ?? 0;
  const dtiImpact = dtiRatio <= 36 ? 50 : dtiRatio <= 43 ? 25 : dtiRatio <= 50 ? 0 : -50;

  // Calculate final score
  let score = Math.round(baseScore + paymentImpact + dtiImpact);

  // Ensure score stays within valid range
  score = Math.max(300, Math.min(820, score));

  // Add deterministic variation based on member ID to make it unique per member
  const memberSpecificVariation = latest.uuid ? (parseInt(latest.uuid.replace(/[^0-9]/g, ''), 10) % 20) - 10 : 0;
  score += memberSpecificVariation;

  return score;
};

// Helper function to get credit score status
const getCreditScoreStatus = (score: number) => {
  if (score >= 740) return { label: 'Excellent', color: '#10B981' }; // emerald-500
  if (score >= 670) return { label: 'Good', color: '#0EA5E9' }; // sky-500
  if (score >= 580) return { label: 'Fair', color: '#F59E0B' }; // amber-500
  return { label: 'Poor', color: '#EF4444' }; // red-500
};

export const CreditScoreChart: FC<CreditScoreChartProps> = ({
  data,
  showDebug,
  onExpandClick,
  monthsToShow = 12,
  hideHeader = false
}) => {
  // Ensure data is sorted chronologically
  const sortedData = [...data].sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  );
  
  // Get credit scores for each 3-month period
  let creditScores = sortedData.reduce((acc: CreditScoreData[], stat, index) => {
    const date = parseISO(stat.month);
    const monthsFromStart = differenceInMonths(date, parseISO(sortedData[0].month));
    if (monthsFromStart % 3 === 0) {
      acc.push({
        month: stat.month,
        credit_score: calculateMockCreditScore([...sortedData].slice(0, index + 1)),
      });
    }
    return acc;
  }, []);
  
  // Always include the most recent month if it's not already included
  const mostRecentMonth = sortedData[sortedData.length - 1]?.month;
  const currentScore = calculateMockCreditScore(sortedData);
  
  if (mostRecentMonth && !creditScores.find(score => score.month === mostRecentMonth)) {
    creditScores.push({
      month: mostRecentMonth,
      credit_score: currentScore
    });
  }
  
  // Format number for Y-axis
  const formatNumber = (value: number) => value.toString();
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`${value}`, 'Credit Score'];
  
  // Prepare chart datasets including the main line and trend line
  const getChartDatasets = () => {
    if (!creditScores.length) return [];
    
    // Extract credit score values
    const values = creditScores.map(d => d.credit_score);
    
    // Calculate trend line using linear regression
    const calculateTrendLine = (data: number[]): number[] => {
      if (data.length <= 2) return data;
      
      // Use linear regression to calculate trend line
      let sumX = 0;
      let sumY = 0;
      let sumXY = 0;
      let sumXX = 0;
      const n = data.length;
      
      // Calculate sums for linear regression formula
      for (let i = 0; i < n; i++) {
        sumX += i;
        sumY += data[i];
        sumXY += i * data[i];
        sumXX += i * i;
      }
      
      // Calculate slope and y-intercept
      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      
      // Generate trend line points
      return data.map((_, i) => intercept + slope * i);
    };
    
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Credit Score',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: '#64748B',
        pointBorderWidth: 2,
        borderColor: '#64748B',
        backgroundColor: 'rgba(100, 116, 139, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  };
  
  // Get the formatted current month
  const getCurrentMonthFormatted = (stats: FinancialHealthStats[]): string => {
    if (!stats.length) return 'N/A';
    try {
      const latestStat = stats[stats.length - 1];
      return format(parseISO(latestStat.month), 'MMMM yyyy');
    } catch (e) {
      if (import.meta.env.DEV) console.error('Error formatting date:', e);
      return 'N/A';
    }
  };
  
  // Render the overlay with current data
  const renderOverlay = (_visibleData: any[]): ReactNode => {
    // Current month formatted
    const currentMonth = getCurrentMonthFormatted(sortedData);
    
    // Current score
    const status = getCreditScoreStatus(currentScore);
    
    // Calculate previous score (3 months ago)
    const previousScore = creditScores.length > 1 
      ? creditScores[creditScores.length - 2]?.credit_score 
      : currentScore;
    
    const change = currentScore - previousScore;
    
    return (
      <div 
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">
              Credit Score - {currentMonth}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-slate-700">
                {Math.round(currentScore)}
              </div>
              <div
                className="text-sm font-medium px-2 py-1 rounded-full"
                style={{ 
                  backgroundColor: `${status.color}20`, 
                  color: status.color 
                }}
              >
                {status.label}
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
            <div>
              <div className="text-xs text-slate-500">3-Month Change</div>
              <div className="flex items-center gap-2">
                <div
                  className="text-2xl font-semibold"
                  style={{ color: change >= 0 ? '#10B981' : '#EF4444' }}
                >
                  {change >= 0 ? '↑' : '↓'}
                </div>
                <div className="text-sm font-medium">
                  {Math.abs(change)} points
                </div>
              </div>
            </div>
            
            <div>
              <div className="text-xs text-slate-500">Next Threshold</div>
              <div className="text-sm font-medium">
                {status.label !== 'Excellent' 
                  ? `${status.label === 'Poor' ? '580' : status.label === 'Fair' ? '670' : '740'} for ${
                      status.label === 'Poor' ? 'Fair' : status.label === 'Fair' ? 'Good' : 'Excellent'
                    }`
                  : 'Maximum Rating Achieved'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <BaseChartTemplate<CreditScoreData>
      data={creditScores}
      showDebug={showDebug}
      monthsToShow={monthsToShow}
      onExpandClick={onExpandClick}
      title="Credit Score"
      dataKey="credit_score"
      color="#64748B" // slate-500
      yAxisFormatter={formatNumber}
      tooltipFormatter={formatTooltip}
      renderOverlay={renderOverlay}
      min={300}
      max={850}
      isCurrentFinances={true}
      pointRadius={3}
      pointBackgroundColor="rgba(255, 255, 255, 0.9)"
      pointBorderColor="#64748B"
      pointBorderWidth={2}
      borderWidth={2.5}
      tension={LINE_STYLES.CURVED}
      customDatasets={getChartDatasets()}
      hideHeader={hideHeader}
      yAxisStepSize={50}
      chartHeight="360px"
    />
  );
}; 